import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, EyeIcon, PencilIcon, TrashIcon, PlayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { abacPolicyService } from '@/services/abac';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

interface Policy {
  id: string;
  name: string;
  description: string;
  effect: 'ALLOW' | 'DENY';
  status: 'ACTIVE' | 'INACTIVE' | 'DRAFT';
  priority: number;
  condition_count: number;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  version: string;
  tags?: string[];
  compliance_frameworks?: string[];
}

export const PolicyManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await abacPolicyService.getAll();
      if (response.success) {
        setPolicies(response.data);
      }
    } catch (err) {
      setError(t('common.errorLoadingData'));
      console.error('Error fetching policies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePolicy = async (policyId: string) => {
    if (!confirm(t('common.confirmDelete'))) return;
    
    try {
      await abacPolicyService.delete(policyId);
      await fetchPolicies();
    } catch (err) {
      console.error('Error deleting policy:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      ACTIVE: { color: 'green', text: t('common.active') },
      INACTIVE: { color: 'gray', text: t('common.inactive') },
      DRAFT: { color: 'yellow', text: t('common.draft') }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.INACTIVE;
    return <Badge color={config.color as any}>{config.text}</Badge>;
  };

  const getEffectBadge = (effect: string) => {
    const effectConfig = {
      ALLOW: { color: 'green', text: t('common.allow') },
      DENY: { color: 'red', text: t('common.deny') }
    };
    
    const config = effectConfig[effect as keyof typeof effectConfig] || effectConfig.ALLOW;
    return <Badge color={config.color as any}>{config.text}</Badge>;
  };

  const filteredPolicies = policies.filter(policy => {
    const matchesStatus = filterStatus === 'ALL' || policy.status === filterStatus;
    const matchesSearch = policy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         policy.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
            {t('abac.policyManagement.title')}
          </h1>
          <p className="text-[var(--color-secondary-text)]">
            {t('abac.policyManagement.description')}
          </p>
        </div>
        <Link to="/abac/policies/create">
          <Button className="flex items-center space-x-2">
            <PlusIcon className="w-4 h-4" />
            <span>{t('abac.policyManagement.createPolicy')}</span>
          </Button>
        </Link>
      </div>

      {/* Filters and Search */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
          >
            <option value="ALL">{t('common.allStatuses')}</option>
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="INACTIVE">{t('common.inactive')}</option>
            <option value="DRAFT">{t('common.draft')}</option>
          </select>
        </div>
      </Card>

      {/* Policies List */}
      {error && (
        <Card className="p-4 border-red-500">
          <p className="text-red-600">{error}</p>
        </Card>
      )}

      <div className="space-y-4">
        {filteredPolicies.length === 0 ? (
          <Card className="p-8 text-center">
            <DocumentTextIcon className="w-12 h-12 mx-auto text-[var(--color-secondary-text)] mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-secondary-text)] mb-2">
              {t('abac.policyManagement.noPolicies')}
            </h3>
            <p className="text-[var(--color-secondary-text)] mb-4">
              {t('abac.policyManagement.noPoliciesDescription')}
            </p>
            <Link to="/abac/policies/create">
              <Button className="flex items-center space-x-2 mx-auto">
                <PlusIcon className="w-4 h-4" />
                <span>{t('abac.policyManagement.createFirstPolicy')}</span>
              </Button>
            </Link>
          </Card>
        ) : (
          filteredPolicies.map((policy) => (
            <Card key={policy.id} className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-[var(--color-secondary-text)]">
                      {policy.name}
                    </h3>
                    {getStatusBadge(policy.status)}
                    {getEffectBadge(policy.effect)}
                    <Badge color="blue">{t('abac.policyManagement.priority')}: {policy.priority}</Badge>
                  </div>
                  <p className="text-[var(--color-secondary-text)] mb-3">
                    {policy.description}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-[var(--color-secondary-text)]">
                    <span>{t('abac.policyManagement.conditions')}: {policy.condition_count}</span>
                    <span>{t('abac.policyManagement.version')}: {policy.version}</span>
                    <span>{t('abac.policyManagement.createdBy')}: {policy.created_by_email}</span>
                    <span>{t('abac.policyManagement.updated')}: {new Date(policy.updated_at).toLocaleDateString()}</span>
                  </div>
                  {policy.tags && policy.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {policy.tags.map((tag, index) => (
                        <Badge key={index} color="gray" variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Link to={`/abac/policies/${policy.id}`}>
                    <Button variant="outline" size="sm">
                      <EyeIcon className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={`/abac/policies/${policy.id}/edit`}>
                    <Button variant="outline" size="sm">
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={`/abac/policies/${policy.id}/test`}>
                    <Button variant="outline" size="sm">
                      <PlayIcon className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeletePolicy(policy.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
